import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  clearActionMeta,
  dataMetaSelector,
  eventBlockSelectorCreator,
} from "Bands/state";
import { BAND_ACTIONS, EventBlocks, IEventInfoBlock } from "Bands/types";
import { useClearActionMetaOnUnMount, useEventRoute } from "Bands/utils";
import { IAPIState } from "shared/types";
import { useEventContext } from "Bands/contexts";
import { EditInfoBlock } from "Bands/containers";
import { BAND_PATHS } from "Bands/router";
import { FourOhFour, FourOhThree } from "shared/pages";
import { Card, Loader } from "shared/components";
import { copy } from "shared/assets";
import { mapUserPermissions } from "shared/utils";

export const EditEventInfoBlockPage = () => {
  let { blockId, blockTypeId } =
    useParams<{ blockTypeId: string; blockId: string }>();
  const { pushRoute } = useEventRoute();

  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editEventInfoBlock)
  );

  const { eventId } = useEventContext();

  const infoBlockSelector = eventBlockSelectorCreator(
    blockId,
    EventBlocks.infoBlock
  );
  const infoBlock = useSelector(infoBlockSelector) as IEventInfoBlock;

  useClearActionMetaOnUnMount(BAND_ACTIONS.editEventInfoBlock);

  useEffect(() => {
    if (!blockId || !eventId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editEventInfoBlock));
    }
  }, [dispatch, apiMeta, blockId, eventId]);

  // redirect to create page if block is deleted
  const { api: deleteApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.deleteEventInfoBlock)
  );
  useEffect(() => {
    if (!blockTypeId) return;

    if (deleteApiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.deleteEventInfoBlock));

      pushRoute(BAND_PATHS.CREATE_INFO_BLOCK, {
        params: { blockTypeId },
      });
    }
  }, [deleteApiMeta?.status, dispatch, blockTypeId, pushRoute]);

  const { api: getApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventInfoBlocks)
  );

  const renderContent = () => {
    // fetching
    if (!infoBlock)
      return (
        <Card title={`${copy.actions.editing}...`}>
          <Loader />
        </Card>
      );

    return (
      <EditInfoBlock
        infoBlockType={infoBlock.type}
        infoBlock={infoBlock}
        handleFinishEditing={() => pushRoute(BAND_PATHS.BAND_EVENT)}
      />
    );
  };

  if (getApiMeta?.status !== IAPIState.pending && !infoBlock)
    return <FourOhFour />;

  if (infoBlock && !mapUserPermissions(infoBlock?.permissions || []).EDIT) {
    return <FourOhThree />;
  }

  return <>{renderContent()}</>;
};
